/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 9 Records: one current record per movement, grouped by the radar
 * area of each movement in the registry, in radar order (as the app's radar).
 * A movement missing from the registry lands under "Other records".
 */
import {
  MOVEMENT_MEASURE_TYPE,
  RADAR_AXES,
  RADAR_AXIS_DISPLAY_NAMES,
  type RadarAxis,
} from "@lemonadetraining/contracts";
import type { StateRecord } from "./movementStateRecords";
import { currentValueLabel, formatMeasureValue } from "./measureFormat";

export interface RecordRow {
  key: string;
  movementID: string;
  name: string;
  value: string;
  statLabel: string;
  performedAt: Date | null;
}

export interface RecordGroup {
  axis: RadarAxis | null;
  label: string;
  rows: RecordRow[];
}

const OTHER_LABEL = "Other records";

const isRadarAxis = (v: unknown): v is RadarAxis => RADAR_AXES.includes(v as RadarAxis);

function toRow(record: StateRecord): RecordRow {
  const measure = record.measureType;
  const amount = record.value > 0 ? record.value : null;
  let value = "";
  if (measure === MOVEMENT_MEASURE_TYPE.BINARY) value = amount ? "Unlocked" : "Not yet";
  else if (amount != null) value = formatMeasureValue(amount, measure);
  return {
    key: record.movementRegistryID,
    movementID: record.movementRegistryID,
    name: record.name,
    value,
    statLabel: currentValueLabel(measure, record.kind),
    performedAt: record.performedAt ?? null,
  };
}

/** `current` holds one record per movement. `query` matches the movement name. */
export function recordsByAxis(current: readonly StateRecord[], query = ""): RecordGroup[] {
  const q = query.trim().toLowerCase();
  const groups = new Map<RadarAxis | null, RecordRow[]>();
  for (const record of current) {
    const row = toRow(record);
    if (q && !row.name.toLowerCase().includes(q)) continue;
    const axis = isRadarAxis(record.radarAxis) ? record.radarAxis : null;
    const rows = groups.get(axis) ?? [];
    rows.push(row);
    groups.set(axis, rows);
  }

  const ordered: Array<RadarAxis | null> = [...RADAR_AXES, null];
  return ordered
    .filter((axis) => (groups.get(axis) ?? []).length > 0)
    .map((axis) => ({
      axis,
      label: axis ? RADAR_AXIS_DISPLAY_NAMES[axis] : OTHER_LABEL,
      rows: (groups.get(axis) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    }));
}
